import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Receipt, Ban, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PeriodSelector } from "@/components/dashboard/PeriodSelector";
import { SalespersonFilter } from "@/components/dashboard/SalespersonFilter";
import { SaleFormModal } from "@/components/sales/SaleFormModal";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/StateViews";
import { fmtBRL, fmtPct } from "@/lib/format";

const STATUS_OPTIONS = [
  { value: "all", label: "Todos os status" },
  { value: "confirmada", label: "Confirmada" },
  { value: "cancelada", label: "Cancelada" },
];

export default function Sales() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();

  const [period, setPeriod] = useState("month");
  const [salespersonId, setSalespersonId] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const { data: spData } = useQuery({
    queryKey: ["salespeople", "month"],
    queryFn: () => api.get("/salespeople", { params: { period: "month" } }).then((r) => r.data),
  });
  const salespeople = spData?.salespeople || [];

  const params = {
    period,
    page,
    page_size: 20,
    salesperson_id: salespersonId === "all" ? undefined : salespersonId,
    status: status === "all" ? undefined : status,
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["sales", params],
    queryFn: () => api.get("/sales", { params }).then((r) => r.data),
    keepPreviousData: true,
  });

  const invalidateAll = () => {
    ["sales", "dashboard", "salespeople", "ranking", "raiox"].forEach((k) =>
      qc.invalidateQueries({ queryKey: [k] })
    );
  };

  const createMut = useMutation({
    mutationFn: (payload) => api.post("/sales", payload).then((r) => r.data),
    onSuccess: () => { toast.success("Venda registrada."); invalidateAll(); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/sales/${id}`, payload).then((r) => r.data),
    onSuccess: () => { toast.success("Venda atualizada."); invalidateAll(); },
  });
  const cancelMut = useMutation({
    mutationFn: (id) => api.patch(`/sales/${id}/cancel`).then((r) => r.data),
    onSuccess: () => { toast.success("Venda cancelada."); invalidateAll(); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (sale) => { setEditing(sale); setModalOpen(true); };
  const handleSubmit = (payload) =>
    editing
      ? updateMut.mutateAsync({ id: editing.id, payload })
      : createMut.mutateAsync(payload);

  const rows = data?.items || [];

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            <Receipt className="h-7 w-7 text-primary" />
            Vendas
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Registro operacional de vendas</p>
        </div>
        {isAdmin && (
          <Button data-testid="new-sale-button" onClick={openNew} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" /> Nova venda
          </Button>
        )}
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-wrap gap-3">
        <SalespersonFilter
          value={salespersonId}
          onChange={(v) => { setSalespersonId(v); setPage(1); }}
          options={salespeople}
        />
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="status-filter" className="h-10 w-[180px] bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`status-option-${s.value}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <PeriodSelector value={period} onChange={(v) => { setPeriod(v); setPage(1); }} />
      </div>

      {isLoading && <LoadingState label="Carregando vendas..." />}
      {isError && <ErrorState message={formatApiErrorDetail(error?.response?.data?.detail)} onRetry={refetch} />}

      {data && !isError && (
        <div className={isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}>
          {rows.length === 0 ? (
            <EmptyState message="Nenhuma venda encontrada para os filtros." />
          ) : (
            <div className="overflow-x-auto rounded-md border border-border" data-testid="sales-table">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Data</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="text-right">Receita</TableHead>
                    <TableHead className="text-right">Custo</TableHead>
                    <TableHead className="text-right">Lucro</TableHead>
                    <TableHead className="text-right">Margem</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin && <TableHead className="text-right">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <TableRow key={s.id} data-testid={`sale-row-${s.id}`} className={s.status === "cancelada" ? "opacity-60" : ""}>
                      <TableCell className="font-num whitespace-nowrap">{s.date.split("-").reverse().join("/")}</TableCell>
                      <TableCell className="font-medium">{s.salesperson_name}</TableCell>
                      <TableCell className="text-muted-foreground">{s.client || "—"}</TableCell>
                      <TableCell className="text-right font-num">{fmtBRL(s.revenue)}</TableCell>
                      <TableCell className="text-right font-num text-muted-foreground">{fmtBRL(s.cost)}</TableCell>
                      <TableCell className="text-right font-num">{fmtBRL(s.lucro)}</TableCell>
                      <TableCell className="text-right font-num text-muted-foreground">{fmtPct(s.margem)}</TableCell>
                      <TableCell>
                        <Badge
                          data-testid={`sale-status-${s.id}`}
                          variant={s.status === "cancelada" ? "outline" : "default"}
                          className={s.status === "cancelada" ? "border-destructive/50 text-destructive" : "bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]"}
                        >
                          {s.status === "cancelada" ? "Cancelada" : "Confirmada"}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button data-testid={`edit-sale-${s.id}`} variant="ghost" size="icon" className="h-8 w-8" disabled={s.status === "cancelada"} onClick={() => openEdit(s)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button data-testid={`cancel-sale-${s.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" disabled={s.status === "cancelada"} onClick={() => setCancelTarget(s)}>
                              <Ban className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginação */}
          {data.pages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
              <span>Página {data.page} de {data.pages} · {data.total} vendas</span>
              <div className="flex gap-2">
                <Button data-testid="prev-page" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
                <Button data-testid="next-page" variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <SaleFormModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          sale={editing}
          salespeople={salespeople}
          onSubmit={handleSubmit}
          submitting={createMut.isPending || updateMut.isPending}
        />
      )}

      <AlertDialog open={Boolean(cancelTarget)} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar venda?</AlertDialogTitle>
            <AlertDialogDescription>
              A venda ficará com status "Cancelada" e deixará de contar nos indicadores. O histórico é preservado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-cancel-sale"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { cancelMut.mutate(cancelTarget.id); setCancelTarget(null); }}
            >
              Confirmar cancelamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
